import MappingView from "./MappingView";
import ParseDatasetView from "./ParseDatasetView";
import SummaryView from "./SummaryView";
import posthogEvents from "@app/config/posthog/posthog-events";
import { getDatasetSpaceDetails, usePostDataset } from "@app/queries/datasets";
import { useCreateExperimentDraft } from "@app/queries/experiment";
import { datasetCreateInputSchema } from "@app/schemas/schemas";
import type { CSVParseOutput } from "@globals";
import getDatasetFromMapping from "@lib/dataset/Mapping/getDatasetFromMapping";
import type { ColumnMapping } from "@lib/dataset/Mapping/types";
import { createDraftInputFromDatasetSpace } from "@lib/experiment/utils/createDraftExperimentFromDatasetSpace";
import { notifications } from "@lib/notification/notification";
import { ContextualError } from "@lib/utils/ContextualError";
import { Alert, List, LoadingOverlay, Stack, Transition } from "@mantine/core";
import type { FileWithPath } from "@mantine/dropzone";
import { usePostHog } from "posthog-js/react";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router";
import { type BaseIssue, safeParse } from "valibot";

enum ViewState {
  PARSE = "parse",
  MAPPING = "mapping",
  SUMMARY = "summary",
  ERROR = "error"
}

export default function UploadDatasetModal({ file, onClose }: { file: FileWithPath; onClose?: () => void }) {
  const [viewState, setViewState] = useState<ViewState>(ViewState.PARSE);
  const [columnMappingItems, setColumnMappingItems] = useState<ColumnMapping[] | null>(null);
  const [validationIssues, setValidationIssues] = useState<BaseIssue<unknown>[] | null>(null);
  const [parseIssues, setParseIssues] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const posthog = usePostHog();
  const navigate = useNavigate();
  const postDataset = usePostDataset();
  const { mutateAsync: createDraftMutation } = useCreateExperimentDraft();
  const onParseComplete = useCallback(
    ({ parsing_results }: CSVParseOutput) => {
      if (parsing_results.length > 0) {
        setColumnMappingItems(
          parsing_results.map(parsingResult => {
            const isCategorical = !!(parsingResult.categories && parsingResult.categories.length > 0);
            return {
              type: parsingResult.column_inferred_type,
              isCategorical,
              disabled: false,
              id: crypto.randomUUID(),
              ...parsingResult
            };
          }) satisfies ColumnMapping[]
        );
      }

      if (parsing_results.some(result => result.valid === false)) {
        setParseIssues(true);
      } else {
        setParseIssues(false);
      }

      setViewState(ViewState.MAPPING);
    },
    [setColumnMappingItems, setParseIssues, setViewState]
  );

  const onMappingComplete = useCallback(() => {
    setParseIssues(false); // User has seen issue and decided to proceed
    setViewState(ViewState.SUMMARY);
  }, [setViewState]);

  const onFinish = useCallback(
    async ({ createExperiment, datasetName }: { createExperiment?: boolean; datasetName: string }) => {
      posthog.capture(posthogEvents.upload_dataset_modal.dataset_create, { withCreateExperiment: createExperiment });
      setParseIssues(false);
      setValidationIssues(null);

      if (!columnMappingItems) {
        throw new ContextualError("onFinish called but columnMappingItems is null", {
          view_state: viewState,
          file_name: file.name
        });
      }

      // Discard any column that is considered not valid (Lacks datapoints)
      const items = columnMappingItems.filter(item => item.valid);

      try {
        const dataset = getDatasetFromMapping(items, datasetName);
        const parse = safeParse(datasetCreateInputSchema, dataset);

        if (!parse.success) {
          console.error("Error during dataset parse:", parse.issues);
          setViewState(ViewState.ERROR);
          setValidationIssues(parse.success ? null : parse.issues);
          return;
        }

        setIsLoading(true);
        const createdDataset = await postDataset.mutateAsync({ body: parse.output });
        notifications.success("Dataset created successfully");

        if (createExperiment) {
          const datasetSpaceDetails = await getDatasetSpaceDetails(createdDataset.id);
          const draftInput = createDraftInputFromDatasetSpace(datasetSpaceDetails);
          const draft = await createDraftMutation({ body: draftInput });
          await navigate(`/experiments/drafts/${draft.id}`);
        } else {
          await navigate(`/datasets/${createdDataset.id}`);
        }

        onClose?.();
      } catch (error) {
        console.error("Error during dataset upload:", error);
        setViewState(ViewState.ERROR);
      } finally {
        setIsLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columnMappingItems, postDataset, navigate, createDraftMutation, onClose]
  );

  const onBack = (state: ViewState) => {
    setValidationIssues(null);
    setViewState(state);
  };

  return (
    <Stack>
      <LoadingOverlay visible={isLoading} />
      <Stack pos="relative" gap={0} miw={400}>
        <Transition transition="fade" mounted={Boolean(parseIssues)}>
          {styles => (
            <Alert
              mt="md"
              title="We found some issues with your dataset, please check the status column to know more."
              radius={0}
              style={styles}
            ></Alert>
          )}
        </Transition>
        <Transition transition="fade" mounted={Boolean(validationIssues)}>
          {styles => (
            <Alert radius={0} style={styles} m="md" color="orange">
              <List m="xs">
                {validationIssues &&
                  validationIssues.map((issue, index) => {
                    return <List.Item key={index}>{issue.message}</List.Item>;
                  })}
              </List>
            </Alert>
          )}
        </Transition>
        {viewState === ViewState.PARSE && <ParseDatasetView file={file} onComplete={onParseComplete} />}
        {viewState === ViewState.MAPPING && columnMappingItems && (
          <MappingView items={columnMappingItems} onChange={setColumnMappingItems} onComplete={onMappingComplete} />
        )}
        {viewState === ViewState.SUMMARY && columnMappingItems && (
          <SummaryView items={columnMappingItems} onComplete={onFinish} onBack={() => onBack(ViewState.MAPPING)} />
        )}
        {viewState === ViewState.ERROR && columnMappingItems && (
          <>
            <SummaryView items={columnMappingItems} onComplete={onFinish} onBack={() => onBack(ViewState.MAPPING)} />
          </>
        )}
      </Stack>
    </Stack>
  );
}
